import React, { useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, IconButton, MenuItem, Stack, TextField,
  Typography, Accordion, AccordionSummary, AccordionDetails, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useGrowthPlannerStore } from '../store/growthPlannerStore';
import { ProgressBar, SectionHeader } from '../components/ui';
import { ROUTE_STATUS, PRIORITY, ITEM_STATUS, ITEM_TYPES } from '../utils/schema';
import { resolveStageItems, computeStageEstimatedHours } from '../utils/learningResources';

export default function RouteDetail({ routeId, onBack }) {
  const route = useGrowthPlannerStore((s) => s.routes.find((r) => r.id === routeId));
  const learningResources = useGrowthPlannerStore((s) => s.learningResources);
  const updateRoute = useGrowthPlannerStore((s) => s.updateRoute);
  const addStage = useGrowthPlannerStore((s) => s.addStage);
  const updateStage = useGrowthPlannerStore((s) => s.updateStage);
  const deleteStage = useGrowthPlannerStore((s) => s.deleteStage);
  const addItem = useGrowthPlannerStore((s) => s.addItem);
  const updateItem = useGrowthPlannerStore((s) => s.updateItem);
  const deleteItem = useGrowthPlannerStore((s) => s.deleteItem);

  const [stageDialog, setStageDialog] = useState(false);
  const [itemDialog, setItemDialog] = useState({ open: false, stageId: null });
  const [stageForm, setStageForm] = useState({ title: '', description: '', deadline: '', expectedOutcomes: '' });
  const [itemForm, setItemForm] = useState({
    type: 'course', title: '', institution: '', url: '', estimatedHours: '',
    targetDate: '', skills: '', notes: '',
  });

  if (!route) {
    return (
      <Box textAlign="center" py={4}>
        <Typography color="text.secondary">Ruta no encontrada</Typography>
        <Button onClick={onBack} sx={{ mt: 2 }}>Volver</Button>
      </Box>
    );
  }

  const handleAddStage = () => {
    if (!stageForm.title.trim()) return;
    addStage(routeId, stageForm);
    setStageForm({ title: '', description: '', deadline: '', expectedOutcomes: '' });
    setStageDialog(false);
  };

  const handleAddItem = () => {
    if (!itemForm.title.trim() || !itemDialog.stageId) return;
    addItem(routeId, itemDialog.stageId, {
      ...itemForm,
      estimatedHours: Number(itemForm.estimatedHours) || 0,
      skills: itemForm.skills.split(',').map((s) => s.trim()).filter(Boolean),
    });
    setItemForm({ type: 'course', title: '', institution: '', url: '', estimatedHours: '', targetDate: '', skills: '', notes: '' });
    setItemDialog({ open: false, stageId: null });
  };

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>Volver a rutas</Button>

      <SectionHeader
        title={route.name}
        subtitle={route.targetRole || route.objective}
        action={
          <Stack direction="row" spacing={1}>
            <Chip label={ROUTE_STATUS[route.status]?.label} color={ROUTE_STATUS[route.status]?.color} />
            <TextField
              select size="small" label="Estado" value={route.status}
              onChange={(e) => updateRoute(routeId, { status: e.target.value })}
              sx={{ minWidth: 130 }}
            >
              {Object.entries(ROUTE_STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
            </TextField>
          </Stack>
        }
      />

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <ProgressBar value={route.progress || 0} label="Progreso general" />
          {route.notes && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{route.notes}</Typography>
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          <Stack spacing={1}>
            <Chip label={`Prioridad: ${PRIORITY[route.priority]?.label}`} variant="outlined" />
            <Chip label={`${route.investedHours || 0}h / ${route.estimatedHours || 0}h estimadas`} variant="outlined" />
            {route.targetDate && <Chip label={`Meta: ${route.targetDate}`} variant="outlined" color="primary" />}
          </Stack>
        </Grid>
      </Grid>

      <TextField
        label="Notas personales"
        multiline rows={2}
        value={route.notes || ''}
        onChange={(e) => updateRoute(routeId, { notes: e.target.value })}
        fullWidth
        sx={{ mb: 3 }}
      />

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>Etapas</Typography>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setStageDialog(true)}>
          Agregar etapa
        </Button>
      </Stack>

      {(route.stages || []).length === 0 ? (
        <Card variant="outlined" sx={{ borderRadius: 2, textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">Ej: Fundamentos → Especialización → Liderazgo</Typography>
        </Card>
      ) : (
        route.stages.map((stage, idx) => (
          <Accordion key={stage.id} defaultExpanded={idx === 0} sx={{ mb: 1, borderRadius: '8px !important', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
                <Typography fontWeight={600} sx={{ flex: 1 }}>
                  Etapa {idx + 1} — {stage.title}
                </Typography>
                <Chip label={`${stage.progress || 0}%`} size="small" color="primary" />
                <Chip label={`${computeStageEstimatedHours(stage, learningResources)}h`} size="small" variant="outlined" />
                {stage.deadline && <Chip label={stage.deadline} size="small" variant="outlined" />}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <TextField
                  label="Descripción" size="small" fullWidth
                  value={stage.description || ''}
                  onChange={(e) => updateStage(routeId, stage.id, { description: e.target.value })}
                />
                <TextField
                  label="Resultados esperados" size="small" fullWidth multiline rows={2}
                  value={stage.expectedOutcomes || ''}
                  onChange={(e) => updateStage(routeId, stage.id, { expectedOutcomes: e.target.value })}
                />
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Deadline" type="date" size="small"
                    value={stage.deadline || ''}
                    onChange={(e) => updateStage(routeId, stage.id, { deadline: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    select label="Prioridad" size="small" value={stage.priority || 'medium'}
                    onChange={(e) => updateStage(routeId, stage.id, { priority: e.target.value })}
                    sx={{ minWidth: 120 }}
                  >
                    {Object.entries(PRIORITY).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                  </TextField>
                  <IconButton color="error" onClick={() => deleteStage(routeId, stage.id)}><DeleteIcon /></IconButton>
                </Stack>

                <Divider />

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" fontWeight={600}>Cursos / Certificaciones / Recursos</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={() => setItemDialog({ open: true, stageId: stage.id })}>
                    Agregar
                  </Button>
                </Stack>

                {resolveStageItems(stage, learningResources).map((item) => {
                  const placement = (stage.items || []).find((p) => p.id === item.placementId);
                  if (!placement) return null;
                  return (
                  <Card key={placement.id} variant="outlined" sx={{ borderRadius: 1.5 }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ flex: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography variant="body2">{ITEM_TYPES[item.type]?.icon}</Typography>
                            <Typography variant="body2" fontWeight={600}>{item.title}</Typography>
                            <Chip label={ITEM_STATUS[item.status]?.label} size="small" color={ITEM_STATUS[item.status]?.color} />
                            <Chip label={`${Number(item.estimatedHours) || 0}h`} size="small" variant="outlined" />
                          </Stack>
                          {item.institution && (
                            <Typography variant="caption" color="text.secondary">{item.institution}</Typography>
                          )}
                          <ProgressBar value={item.progress || 0} label="" />
                          {item.skills?.length > 0 && (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                              {item.skills.map((sk) => <Chip key={sk} label={sk} size="small" variant="outlined" />)}
                            </Stack>
                          )}
                        </Box>
                        <Stack spacing={0.5} alignItems="flex-end">
                          <TextField
                            select size="small" value={item.status}
                            onChange={(e) => updateItem(routeId, stage.id, placement.id, {
                              status: e.target.value,
                              progress: e.target.value === 'completed' ? 100 : item.progress,
                            })}
                            sx={{ minWidth: 130 }}
                          >
                            {Object.entries(ITEM_STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                          </TextField>
                          <TextField
                            label="%" type="number" size="small" sx={{ width: 70 }}
                            value={item.progress || 0}
                            onChange={(e) => updateItem(routeId, stage.id, placement.id, { progress: Number(e.target.value) })}
                          />
                          <IconButton size="small" color="error" onClick={() => deleteItem(routeId, stage.id, placement.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                  );
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))
      )}

      <Dialog open={stageDialog} onClose={() => setStageDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nueva Etapa</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Título" placeholder="Ej: Fundamentos" value={stageForm.title} onChange={(e) => setStageForm({ ...stageForm, title: e.target.value })} fullWidth />
            <TextField label="Descripción" multiline rows={2} value={stageForm.description} onChange={(e) => setStageForm({ ...stageForm, description: e.target.value })} fullWidth />
            <TextField label="Deadline" type="date" value={stageForm.deadline} onChange={(e) => setStageForm({ ...stageForm, deadline: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth />
            <TextField label="Resultados esperados" multiline rows={2} value={stageForm.expectedOutcomes} onChange={(e) => setStageForm({ ...stageForm, expectedOutcomes: e.target.value })} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStageDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleAddStage} disabled={!stageForm.title.trim()}>Agregar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={itemDialog.open} onClose={() => setItemDialog({ open: false, stageId: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Agregar recurso</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label="Tipo" value={itemForm.type} onChange={(e) => setItemForm({ ...itemForm, type: e.target.value })} fullWidth>
              {Object.entries(ITEM_TYPES).map(([k, v]) => <MenuItem key={k} value={k}>{v.icon} {v.label}</MenuItem>)}
            </TextField>
            <TextField label="Título" value={itemForm.title} onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })} fullWidth />
            <TextField label="Institución / plataforma" value={itemForm.institution} onChange={(e) => setItemForm({ ...itemForm, institution: e.target.value })} fullWidth />
            <TextField label="URL" value={itemForm.url} onChange={(e) => setItemForm({ ...itemForm, url: e.target.value })} fullWidth />
            <Stack direction="row" spacing={2}>
              <TextField label="Horas estimadas" type="number" value={itemForm.estimatedHours} onChange={(e) => setItemForm({ ...itemForm, estimatedHours: e.target.value })} fullWidth />
              <TextField label="Fecha objetivo" type="date" value={itemForm.targetDate} onChange={(e) => setItemForm({ ...itemForm, targetDate: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth />
            </Stack>
            <TextField label="Habilidades (separadas por coma)" value={itemForm.skills} onChange={(e) => setItemForm({ ...itemForm, skills: e.target.value })} fullWidth />
            <TextField label="Notas" multiline rows={2} value={itemForm.notes} onChange={(e) => setItemForm({ ...itemForm, notes: e.target.value })} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemDialog({ open: false, stageId: null })}>Cancelar</Button>
          <Button variant="contained" onClick={handleAddItem} disabled={!itemForm.title.trim()}>Agregar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
